/**
 * useEnrollments Hook
 *
 * React Query hooks for ActivityEnrollments.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notify } from '@/shared/services';
import { useTranslation } from '@/core';
import {
  ActivityEnrollment,
  EnrollmentFilters,
  EnrollmentFormData,
} from '../types';
import {
  fetchEnrollments,
  fetchEnrollment,
  createEnrollment,
  attendEnrollment,
  noShowEnrollment,
  cancelEnrollment,
  promoteEnrollment,
  moveEnrollment,
} from '../services/enrollmentService';
import { activityKeys } from './useActivities';

// ── Query key factory ──────────────────────────────────────────────────────────

export const enrollmentKeys = {
  all: ['activity-enrollments'] as const,
  lists: () => [...enrollmentKeys.all, 'list'] as const,
  list: (filters?: EnrollmentFilters) => [...enrollmentKeys.lists(), filters] as const,
  details: () => [...enrollmentKeys.all, 'detail'] as const,
  detail: (id: string) => [...enrollmentKeys.details(), id] as const,
};

// ── Hooks ──────────────────────────────────────────────────────────────────────

export function useEnrollments(filters?: EnrollmentFilters, options?: { enabled?: boolean }) {
  return useQuery<ActivityEnrollment[]>({
    queryKey: enrollmentKeys.list(filters),
    queryFn: () => fetchEnrollments(filters),
    enabled: options?.enabled ?? true,
  });
}

export function useEnrollment(id: string) {
  return useQuery<ActivityEnrollment>({
    queryKey: enrollmentKeys.detail(id),
    queryFn: () => fetchEnrollment(id),
    enabled: !!id,
  });
}

export function useCreateEnrollment() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: ({ activityId, data }: { activityId: string; data: EnrollmentFormData }) =>
      createEnrollment(activityId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: enrollmentKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: activityKeys.enrollments(variables.activityId),
      });
      notify.success(t('activities.enrollments.created', 'Enrollment created'));
    },
    onError: (error: Error) => {
      notify.error(t('activities.enrollments.createFailed', 'Error enrolling: {{message}}', { message: error.message }));
    },
  });
}

export function useAttendEnrollment() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (id: string) => attendEnrollment(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: enrollmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: enrollmentKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: activityKeys.enrollments(data.activity) });
      notify.success(t('activities.enrollments.attended', 'Attendance recorded'));
    },
    onError: (error: Error) => {
      notify.error(t('activities.enrollments.attendFailed', 'Error recording attendance: {{message}}', { message: error.message }));
    },
  });
}

export function useNoShowEnrollment() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (id: string) => noShowEnrollment(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: enrollmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: enrollmentKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: activityKeys.enrollments(data.activity) });
      notify.success(t('activities.enrollments.noShow', 'Marked as no-show'));
    },
    onError: (error: Error) => {
      notify.error(t('activities.enrollments.noShowFailed', 'Error: {{message}}', { message: error.message }));
    },
  });
}

export function useCancelEnrollment() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: ({ id, voidCharge, voidStaffExpense }: { id: string; voidCharge?: boolean; voidStaffExpense?: boolean }) =>
      cancelEnrollment(id, { voidCharge, voidStaffExpense }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: enrollmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: enrollmentKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: activityKeys.enrollments(data.activity) });
      notify.success(t('activities.enrollments.cancelled', 'Enrollment cancelled'));
    },
    onError: (error: Error) => {
      notify.error(t('activities.enrollments.cancelFailed', 'Error cancelling enrollment: {{message}}', { message: error.message }));
    },
  });
}

export function usePromoteEnrollment() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: ({ id, force }: { id: string; force?: boolean }) => promoteEnrollment(id, { force }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: enrollmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: enrollmentKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: activityKeys.enrollments(data.activity) });
      notify.success(t('activities.enrollments.promoted', 'Guest promoted from the waitlist'));
    },
    onError: (error: Error) => {
      notify.error(t('activities.enrollments.promoteFailed', 'Error promoting guest: {{message}}', { message: error.message }));
    },
  });
}

export function useMoveEnrollment() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: ({ id, targetActivityId }: { id: string; targetActivityId: string }) =>
      moveEnrollment(id, targetActivityId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: enrollmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: enrollmentKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: activityKeys.enrollments(data.activity) });
      // Invalidate all activity enrollment lists — the source session lost a row.
      queryClient.invalidateQueries({ queryKey: activityKeys.all });
      notify.success(t('activities.enrollments.moved', 'Enrollment moved to another session'));
    },
    onError: (error: Error) => {
      notify.error(t('activities.enrollments.moveFailed', 'Error moving enrollment: {{message}}', { message: error.message }));
    },
  });
}
